import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { protocolService } from '../services/protocolService'
import { ComplianceFilters } from '../services/protocolService'
import { formatDate } from '../utils/locale'
import './ProtocolCompliance.css'

const defaultStartDate = () => {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return d.toISOString().split('T')[0]
}
const defaultEndDate = () => new Date().toISOString().split('T')[0]

export function ProtocolCompliance() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [filters, setFilters] = useState<ComplianceFilters>({
    startDate: defaultStartDate(),
    endDate: defaultEndDate(),
  })

  const { data: protocol, isLoading: protocolLoading } = useQuery({
    queryKey: ['protocol', id],
    queryFn: () => protocolService.getById(id!),
    enabled: !!id,
  })

  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['protocol-compliance', id],
    queryFn: () => protocolService.getCompliance(id!),
    enabled: !!id,
  })

  const { data: report } = useQuery({
    queryKey: ['protocol-compliance-report', id, filters],
    queryFn: () => protocolService.getComplianceReport(id!, filters),
    enabled: !!id,
  })

  const isLoading = protocolLoading || metricsLoading

  if (isLoading && !protocol) {
    return <div className="protocol-compliance-loading">Loading compliance data...</div>
  }

  if (!protocol) {
    return (
      <div className="protocol-compliance-error">
        <p>Protocol not found</p>
        <button className="btn-secondary" onClick={() => navigate('/protocols')}>
          Back to Protocols
        </button>
      </div>
    )
  }

  return (
    <div className="protocol-compliance">
      <div className="protocol-compliance-header">
        <button className="btn-back" onClick={() => navigate(`/protocols/${id}`)}>
          ← Back to Protocol
        </button>
        <h1>Compliance: {protocol.name}</h1>
      </div>

      <section className="compliance-filters">
        <h2>Filters</h2>
        <div className="filters-row">
          <div className="filter-group">
            <label htmlFor="startDate">Start Date</label>
            <input
              id="startDate"
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="endDate">End Date</label>
            <input
              id="endDate"
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
          </div>
          <div className="filter-group filter-group-wide">
            <label htmlFor="patientIds">Patient IDs (comma-separated, optional)</label>
            <input
              id="patientIds"
              type="text"
              value={filters.patientIds?.join(', ') || ''}
              onChange={(e) => {
                const ids = e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
                setFilters({ ...filters, patientIds: ids.length > 0 ? ids : undefined })
              }}
              placeholder="e.g. patient-1, patient-2"
            />
          </div>
        </div>
      </section>

      {isLoading && !metrics ? (
        <div className="protocol-compliance-loading">Loading metrics...</div>
      ) : metrics ? (
        <div className="protocol-compliance-content">
          <section className="metrics-summary">
            <h2>Summary</h2>
            <div className="metrics-grid">
              <div className="metric-card">
                <span className="metric-value">{metrics.totalEnrolled}</span>
                <span className="metric-label">Total Enrolled</span>
              </div>
              <div className="metric-card metric-compliant">
                <span className="metric-value">{metrics.compliantCount}</span>
                <span className="metric-label">Compliant</span>
              </div>
              <div className="metric-card metric-non-compliant">
                <span className="metric-value">{metrics.nonCompliantCount}</span>
                <span className="metric-label">Non-Compliant</span>
              </div>
              <div className="metric-card">
                <span className="metric-value">{metrics.averageCompliance.toFixed(1)}%</span>
                <span className="metric-label">Average Compliance</span>
              </div>
            </div>
          </section>

          {metrics.complianceByRule && metrics.complianceByRule.length > 0 && (
            <section className="compliance-by-rule">
              <h2>Compliance by Rule</h2>
              <div className="rules-compliance-list">
                {metrics.complianceByRule.map((rule) => (
                  <div key={rule.ruleId} className="rule-compliance-row">
                    <span className="rule-name">{rule.ruleName}</span>
                    <span className="rule-percentage">{rule.compliancePercentage.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {report?.patientCompliance && report.patientCompliance.length > 0 && (
            <section className="patient-compliance">
              <h2>Patient Compliance</h2>
              <div className="patient-compliance-table-wrapper">
                <table className="patient-compliance-table">
                  <thead>
                    <tr>
                      <th>Patient ID</th>
                      <th>Compliance</th>
                      <th>Completed</th>
                      <th>Overdue</th>
                      <th>Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.patientCompliance.map((pc) => (
                      <tr key={pc.enrollmentId}>
                        <td>{pc.patientId}</td>
                        <td>
                          <span
                            className={
                              pc.compliancePercentage >= 80
                                ? 'compliance-good'
                                : 'compliance-low'
                            }
                          >
                            {pc.compliancePercentage.toFixed(1)}%
                          </span>
                        </td>
                        <td>{pc.completedActions} / {pc.totalActions}</td>
                        <td>{pc.overdueActions}</td>
                        <td>{formatDate(pc.lastUpdated)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {metrics.totalEnrolled === 0 && (
            <section className="empty-state">
              <p>No patients enrolled in this protocol yet. Enroll patients to see compliance metrics.</p>
            </section>
          )}
        </div>
      ) : (
        <div className="protocol-compliance-error">
          <p>Unable to load compliance data</p>
        </div>
      )}
    </div>
  )
}
